import type { Role, SessionGrants, User } from '@shared/types/User';

/**
 * MD-02 — rôle effectif d’un utilisateur pour un dossier.
 * Source : `roles_by_tenant[tenantId]` (octroi explicite), puis rôle global pour le tenant
 * d’origine ou pour un dossier listé dans `accessible_tenant_ids` (ou `'all'`).
 */
export function roleForTenant(user: User, tenantId: string): Role | null {
  const mapped = user.roles_by_tenant?.[tenantId];
  if (mapped) return mapped;

  if (user.tenant_id === tenantId) return user.role;

  // Refus par défaut (P0-6) : sans liste explicite `accessible_tenant_ids`, un utilisateur — même
  // admin — n'accède qu'à SON entreprise. Les autres dossiers exigent un octroi explicite.
  const access = user.accessible_tenant_ids;
  if (access === 'all' || (Array.isArray(access) && access.includes(tenantId))) {
    return user.role;
  }

  return null;
}

/** L’utilisateur peut-il ouvrir ce dossier (accès + rôle) ? */
export function userHasTenantAccess(user: User, tenantId: string): boolean {
  return roleForTenant(user, tenantId) !== null;
}

/** Met à jour le rôle pour un dossier sans perdre les autres. */
export function withRoleForTenant(user: User, tenantId: string, role: Role): User {
  const roles_by_tenant = { ...(user.roles_by_tenant ?? {}), [tenantId]: role };
  const next: User = {
    ...user,
    roles_by_tenant,
    role: user.tenant_id === tenantId ? role : user.role,
  };
  return next;
}

/** Relève les droits d'accès Cabinet d'une fiche utilisateur pour les porter dans la session. */
export function sessionGrantsFromUser(user: User): SessionGrants {
  return {
    home_tenant_id: user.tenant_id,
    home_role: user.role,
    ...(user.accessible_tenant_ids !== undefined
      ? { accessible_tenant_ids: user.accessible_tenant_ids }
      : {}),
    ...(user.roles_by_tenant ? { roles_by_tenant: { ...user.roles_by_tenant } } : {}),
  };
}

/** Même règle que `roleForTenant`, à partir des droits portés par la session (refus par défaut). */
export function roleForTenantFromGrants(grants: SessionGrants, tenantId: string): Role | null {
  const mapped = grants.roles_by_tenant?.[tenantId];
  if (mapped) return mapped;
  if (grants.home_tenant_id === tenantId) return grants.home_role;
  const access = grants.accessible_tenant_ids;
  if (access === 'all' || (Array.isArray(access) && access.includes(tenantId))) return grants.home_role;
  return null;
}
